import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import EventCard from "./EventCard";
import EventForm from "./EventForm";
import Button from "../shared/Button";
import ConfirmationModal from "../shared/ConfirmationModal";
import BaseModal from "../shared/BaseModal";
import Pagination from "../shared/Pagination";
import SearchBar from "../shared/SearchBar";
import Loading from "../shared/Loading";
import "./EventList.css";
import { ShortfyEvent } from "../../types/event.types";
import {
  cancelEvent,
  completeEvent,
  deleteEvent,
  getMyEvents,
} from "../../hooks/useEvent";
import useEventSearch from "../../hooks/useEventSearch";
import { useNotification } from "../../context/NotificationContext";
import { EventStatus } from "../../types/shared.types";

const EventList: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ShortfyEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<ShortfyEvent[]>([]);
  const [searchResults, setSearchResults] = useState<ShortfyEvent[] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<EventStatus | "all">("all");

  const { setNotification } = useNotification();
  const [editingEvent, setEditingEvent] = useState<ShortfyEvent | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<ShortfyEvent | null>(null);
  const [cancellingEvent, setCancellingEvent] = useState<ShortfyEvent | null>(
    null,
  );
  const [completingEvent, setCompletingEvent] = useState<ShortfyEvent | null>(
    null,
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);

    const response = await getMyEvents();

    if (response.success && response.data) {
      setEvents(response.data);
    } else {
      setNotification({
        type: "error",
        message: response.error || "Failed to load events",
      });
    }

    setIsLoading(false);
  }, [setNotification]);

  const getEffectiveStatus = (e: ShortfyEvent) => {
    const t = new Date(e.eventEndDate).getTime();
    const isPast = Number.isFinite(t) && t < Date.now();
    return e.status === EventStatus.Active && isPast
      ? EventStatus.Completed
      : e.status;
  };

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    const statusOrder: Record<EventStatus, number> = {
      [EventStatus.Active]: 0,
      [EventStatus.Completed]: 1,
      [EventStatus.Cancelled]: 2,
    };

    const activeEvents = searchResults ?? events;
    let filtered = [...activeEvents];

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (event) => getEffectiveStatus(event) === statusFilter,
      );
    }

    filtered.sort((a, b) => {
      const statusA = statusOrder[getEffectiveStatus(a)] ?? 3;
      const statusB = statusOrder[getEffectiveStatus(b)] ?? 3;
      if (statusA !== statusB) return statusA - statusB;
      return new Date(b.eventStartDate).getTime() - new Date(a.eventStartDate).getTime();
    });

    setFilteredEvents(filtered);
    setCurrentPage(1);
  }, [events, searchResults, statusFilter]);

  const { handleSearch } = useEventSearch(
    setSearchResults,
    events,
    () => setCurrentPage(1),
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEvents.length / itemsPerPage),
  );
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const paginatedEvents = filteredEvents.slice(startIdx, endIdx);



  const handleEditSuccess = (updatedEvent: ShortfyEvent) => {
    setEvents((prev) =>
      prev.map((e) => (e.eventId === updatedEvent.eventId ? updatedEvent : e)),
    );
    setEditingEvent(null);
    setNotification({
      type: "success",
      message: "Event updated successfully!",
    });
  };

  const handleDelete = async () => {
    if (!deletingEvent) return;

    const response = await deleteEvent(deletingEvent.eventId);

    if (response.success) {
      setEvents((prev) =>
        prev.filter((e) => e.eventId !== deletingEvent.eventId),
      );
      setNotification({
        type: "success",
        message: "Event deleted successfully!",
      });
    } else {
      setNotification({
        type: "error",
        message: response.error || "Failed to delete event",
      });
    }

    setDeletingEvent(null);
  };

  const handleCancel = async () => {
    if (!cancellingEvent) return;

    const response = await cancelEvent(cancellingEvent.eventId);

    if (response.success) {
      setEvents((prev) =>
        prev.map((e) =>
          e.eventId === cancellingEvent.eventId
            ? { ...e, status: EventStatus.Cancelled }
            : e,
        ),
      );
      setNotification({
        type: "success",
        message: "Event cancelled successfully!",
      });
    } else {
      setNotification({
        type: "error",
        message: response.error || "Failed to cancel event",
      });
    }

    setCancellingEvent(null);
  };

  const handleComplete = async () => {
    if (!completingEvent) return;

    const response = await completeEvent(completingEvent.eventId);

    if (response.success) {
      setEvents((prev) =>
        prev.map((e) =>
          e.eventId === completingEvent.eventId
            ? { ...e, status: EventStatus.Completed }
            : e,
        ),
      );
      setNotification({
        type: "success",
        message: "Event marked as completed!",
      });
    } else {
      setNotification({
        type: "error",
        message: response.error || "Failed to complete event",
      });
    }

    setCompletingEvent(null);
  };

  if (isLoading) {
    return <Loading message="Loading events..." onRetry={fetchEvents} />;
  }

  return (
    <div className="event-list-container">
      <div className="event-list-content">
        {events.length === 0 ? (
          <div className="event-list-empty">
            <h3 className="event-list-empty-title">No events yet</h3>
            <p className="event-list-empty-description">
              Create your first event to get started.
            </p>
            <Button onClick={() => navigate("/cgi-events/create")}>
              Create Event
            </Button>
          </div>
        ) : (
          <>
            <div className="event-list-filters">
              <SearchBar
                title={`Events (${filteredEvents.length})`}
                placeholder="Search by name or location"
                searchQuery={searchQuery}
                onSearchQueryChange={(e) => setSearchQuery(e.target.value)}
                onSearch={() => handleSearch(searchQuery)}
              />

              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as EventStatus | "all")
                }
                className="event-list-filter-select"
              >
                <option value="all">All Status</option>
                <option value={EventStatus.Active}>Active</option>
                <option value={EventStatus.Completed}>Completed</option>
                <option value={EventStatus.Cancelled}>Cancelled</option>
              </select>

              <Button onClick={() => navigate("/cgi-events/create")}>
                Create Event
              </Button>
            </div>

            {filteredEvents.length === 0 ? (
              <div className="event-list-empty">
                <h3 className="event-list-empty-title">No events found</h3>
                <p className="event-list-empty-description">
                  Try adjusting your status filter.
                </p>
              </div>
            ) : (
              <div className="event-list-grid">
                {paginatedEvents.map((event) => (
                  <EventCard
                    key={event.eventId}
                    event={event}
                    onEdit={setEditingEvent}
                    onDelete={setDeletingEvent}
                    onCancel={setCancellingEvent}
                    onComplete={setCompletingEvent}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {events.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          setCurrentPage={setCurrentPage}
          startIdx={startIdx}
          endIdx={endIdx}
          totalItems={filteredEvents.length}
        />
      )}

      <BaseModal
        isOpen={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        title="Edit Event"
        showCloseButton
      >
        {editingEvent && (
          <EventForm
            event={editingEvent}
            onSuccess={handleEditSuccess}
            onCancel={() => setEditingEvent(null)}
          />
        )}
      </BaseModal>

      <ConfirmationModal
        isOpen={!!deletingEvent}
        title="Delete Event"
        message={`Are you sure you want to delete "${deletingEvent?.eventName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeletingEvent(null)}
      />

      <ConfirmationModal
        isOpen={!!cancellingEvent}
        title="Cancel Event"
        message={`Are you sure you want to cancel "${cancellingEvent?.eventName}"?`}
        confirmLabel="Cancel Event"
        cancelLabel="Back"
        variant="danger"
        onConfirm={handleCancel}
        onCancel={() => setCancellingEvent(null)}
      />

      <ConfirmationModal
        isOpen={!!completingEvent}
        title="Complete Event"
        message={`Mark "${completingEvent?.eventName}" as completed?`}
        confirmLabel="Mark Complete"
        cancelLabel="Cancel"
        variant="primary"
        onConfirm={handleComplete}
        onCancel={() => setCompletingEvent(null)}
      />
    </div>
  );
};

export default EventList;